import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentCaptions } from "@fortawesome/pro-solid-svg-icons";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { db } from "../../../../config/firebase";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const FEEDBACK_TYPES = ["Bug", "Change"];

// Beta-tester feedback widget: renders a white "Submit Feedback" button that
// opens a modal, and writes submissions to CcpBetaFeedback. `location` is a
// hidden field so the same button can be reused elsewhere later and still
// tag where the feedback came from.
const SubmitFeedbackButton = ({ schoolName, userName, userEmail, location }) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("Bug");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    setType("Bug");
    setMessage("");
    setError("");
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError("Please enter a message before submitting.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await addDoc(collection(db, "CcpBetaFeedback"), {
        school: schoolName || "",
        name: userName || "",
        email: userEmail || "",
        type,
        message: message.trim(),
        location,
        status: "Open",
        created_at: Timestamp.now(),
      });
      setSubmitting(false);
      setOpen(false);
      setType("Bug");
      setMessage("");
      setSnackbar({
        open: true,
        message: "Thanks! Your feedback has been submitted.",
        severity: "success",
      });
    } catch (err) {
      console.error("Error submitting feedback:", err);
      setSubmitting(false);
      setSnackbar({
        open: true,
        message: "Error submitting feedback. Please try again.",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleOpen}
        startIcon={<FontAwesomeIcon icon={faCommentCaptions} />}
        sx={{
          backgroundColor: "#fff",
          color: "#a41e36",
          "&:hover": { backgroundColor: "#f0f0f0" },
        }}
      >
        Submit Feedback
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <ModalDialogTitle onClose={handleClose}>
          Submit Feedback
        </ModalDialogTitle>
        <DialogContent>
          <TextField
            label="School"
            value={schoolName || ""}
            fullWidth
            margin="normal"
            size="small"
            InputProps={{ readOnly: true }}
            sx={{
              "& .MuiInputBase-root": { backgroundColor: "#f5f5f5" },
            }}
          />
          <TextField
            label="Name"
            value={userName || ""}
            fullWidth
            margin="normal"
            size="small"
            InputProps={{ readOnly: true }}
            sx={{
              "& .MuiInputBase-root": { backgroundColor: "#f5f5f5" },
            }}
          />
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>Type</InputLabel>
            <Select
              value={type}
              label="Type"
              onChange={(e) => setType(e.target.value)}
            >
              {FEEDBACK_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Message"
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              if (error) setError("");
            }}
            fullWidth
            multiline
            minRows={4}
            margin="normal"
            size="small"
            error={!!error}
            helperText={error}
          />
        </DialogContent>
        <ModalActions onSave={handleSubmit} saving={submitting} saveLabel="Submit" />
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SubmitFeedbackButton;
